<script setup>
import { computed } from 'vue';
import { Handle, Position } from '@vue-flow/core';
import { NODE_TYPES, DELAY_UNITS, ACTION_TYPES, CONDITION_TYPES } from '../constants';
import { sourceHandlesFor, uid } from '../graph';

const props = defineProps({
  id: { type: String, required: true },
  data: { type: Object, required: true },
  selected: { type: Boolean, default: false },
});

const emit = defineEmits([
  'remove',
  'change',
  'duplicate',
  'add-condition',
  'configure-condition',
]);

const block = computed(() => props.data.block);
const stats = computed(() => props.data.stats || { success: 0, alert: 0, error: 0 });
const definition = computed(() => NODE_TYPES[block.value.type] || {});
const handles = computed(() => sourceHandlesFor(block.value));

const ACCENT = {
  blue: 'rgb(var(--blue-11))',
  amber: 'rgb(var(--amber-11))',
  teal: 'rgb(var(--teal-11))',
  violet: 'rgb(var(--violet-11))',
  iris: 'rgb(var(--iris-11))',
  ruby: 'rgb(var(--ruby-11))',
  slate: 'rgb(var(--slate-11))',
};

const accent = computed(() => definition.value.accent || 'slate');
const iconColor = computed(() => ACCENT[accent.value] || ACCENT.slate);

const handleColor = id => {
  if (id === 'error' || id === 'false') return 'rgb(var(--ruby-9))';
  if (id === 'true') return 'rgb(var(--teal-9))';
  if (id === 'timeout') return 'rgb(var(--amber-9))';
  return 'rgb(var(--blue-9))';
};

const items = computed(() => {
  const opts = block.value.options || {};
  switch (block.value.type) {
    case 'trigger': {
      const kw = opts.keywords || [];
      return [
        {
          icon: 'i-lucide-radio',
          title: kw.length ? kw.join(', ') : null,
          titleKey: kw.length ? null : 'FLOWS.SUMMARY.ANY_MESSAGE',
        },
      ];
    }
    case 'chat':
      return (opts.messages || []).map(m => {
        if (m.name === 'text-input-message')
          return {
            icon: 'i-lucide-keyboard',
            titleKey: 'FLOWS.INSPECTOR.WAIT_REPLY',
            sub: m.options?.parameter ? `→ ${m.options.parameter}` : null,
          };
        if (m.name === 'send-file-message')
          return {
            icon: 'i-lucide-paperclip',
            title: m.options?.url || null,
            titleKey: m.options?.url ? null : 'FLOWS.SUMMARY.NO_TEXT',
          };
        if (m.name === 'send-url-file-message')
          return {
            icon: 'i-lucide-link',
            title: m.options?.url || null,
            titleKey: m.options?.url ? null : 'FLOWS.SUMMARY.NO_TEXT',
          };
        if (m.name === 'send-audio-message')
          return {
            icon: 'i-lucide-mic',
            title: (m.options?.audios || [])[0] || null,
            titleKey: (m.options?.audios || [])[0] ? null : 'FLOWS.SUMMARY.NO_TEXT',
          };
        if (m.name === 'delay-message')
          return {
            icon: 'i-lucide-hourglass',
            title: `${m.options?.seconds || 0}s digitando...`,
          };
        const text = m.options?.text;
        return {
          icon: 'i-lucide-message-circle',
          title: text || null,
          titleKey: text ? null : 'FLOWS.SUMMARY.NO_TEXT',
        };
      });
    case 'action':
      return (opts.actions || []).length
        ? opts.actions.map(a => ({
            icon: 'i-lucide-zap',
            title:
              ACTION_TYPES.find(t => t.value === a.name)?.label || a.name,
          }))
        : [{ icon: 'i-lucide-zap', titleKey: 'FLOWS.SUMMARY.NO_ACTIONS' }];
    case 'condition':
      return (opts.conditions || []).length
        ? opts.conditions.map(c => {
            const ct = CONDITION_TYPES.find(t => t.value === c.name);
            const param = c.options?.parameter || '';
            const valueKey = ct?.valueKey;
            const val = valueKey ? c.options?.[valueKey] || '' : '';
            const label = ct?.label || c.name;
            let desc;
            if (ct?.noParameter) {
              desc = label;
            } else if (param && val) {
              desc = `${param} ${label} ${val}`;
            } else if (param) {
              desc = `${param} ${label}`;
            } else {
              desc = label;
            }
            return {
              icon: 'i-lucide-filter',
              title: desc,
            };
          })
        : [
            {
              icon: 'i-lucide-filter',
              titleKey: 'FLOWS.SUMMARY.NO_CONDITIONS',
            },
          ];
    case 'delay': {
      const d = opts.delay || {};
      const v = Object.values(d.options || {})[0];
      const u = DELAY_UNITS.find(unit => unit.value === d.name);
      if (v && u)
        return [
          { icon: 'i-lucide-clock', text: String(v), unitKey: u.labelKey },
        ];
      return [{ icon: 'i-lucide-clock', titleKey: 'FLOWS.SUMMARY.NO_DELAY' }];
    }
    case 'api':
      return (opts.apis || []).length
        ? opts.apis.map(a => {
            const ao = a.options || {};
            return {
              icon: 'i-lucide-braces',
              title: ao.url ? `${ao.method || 'GET'} ${ao.url}` : null,
              titleKey: ao.url ? null : 'FLOWS.SUMMARY.NO_URL',
            };
          })
        : [
            {
              icon: 'i-lucide-braces',
              titleKey: 'FLOWS.SUMMARY.NO_URL',
            },
          ];
    case 'randomizer':
      return (opts.randomizers || []).length
        ? opts.randomizers.map(b => ({
            icon: 'i-lucide-shuffle',
            title: `${b.name || '?'} · ${b.perc || 0}%`,
          }))
        : [
            {
              icon: 'i-lucide-shuffle',
              titleKey: 'FLOWS.SUMMARY.NO_OPERATIONS',
            },
          ];
    case 'field-operation':
      return (opts.fieldOperations || []).length
        ? opts.fieldOperations.map(o => ({
            icon: 'i-lucide-list-checks',
            title: o.options?.parameter || '?',
          }))
        : [
            {
              icon: 'i-lucide-list-checks',
              titleKey: 'FLOWS.SUMMARY.NO_OPERATIONS',
            },
          ];
    case 'ai-agent': {
      const items = [];
      if (opts.endpoint) {
        items.push({ icon: 'i-lucide-globe', title: opts.endpoint });
      } else {
        items.push({ icon: 'i-lucide-globe', title: 'Endpoint não configurado' });
      }
      const routes = Object.keys(opts.action_routes || {});
      if (routes.length) {
        routes.forEach(action => {
          items.push({ icon: 'i-lucide-arrow-right', title: action });
        });
      }
      return items;
    }
    default:
      return [];
  }
});

const canAddStep = computed(() =>
  ['chat', 'action', 'condition', 'randomizer', 'field-operation'].includes(
    block.value.type
  )
);

const removeItem = idx => {
  const opts = block.value.options;
  switch (block.value.type) {
    case 'chat':
      if (opts.messages && opts.messages.length > 1) opts.messages.splice(idx, 1);
      break;
    case 'action':
      if (opts.actions) opts.actions.splice(idx, 1);
      break;
    case 'condition':
      if (opts.conditions) opts.conditions.splice(idx, 1);
      break;
    case 'randomizer':
      if (opts.randomizers && opts.randomizers.length > 1) opts.randomizers.splice(idx, 1);
      break;
    case 'field-operation':
      if (opts.fieldOperations) opts.fieldOperations.splice(idx, 1);
      break;
  }
  emit('change');
};

const addStep = () => {
  const opts = block.value.options;
  switch (block.value.type) {
    case 'chat':
      if (!opts.messages) opts.messages = [];
      opts.messages.push({
        name: 'send-text-message',
        options: { text: '', buttons: [], breakMessages: false },
      });
      break;
    case 'action':
      if (!opts.actions) opts.actions = [];
      opts.actions.push({ name: 'add_label', options: {} });
      break;
    case 'condition':
      emit('add-condition', props.id);
      return;
    case 'randomizer':
      if (!opts.randomizers) opts.randomizers = [];
      opts.randomizers.push({
        id: uid(),
        name: String.fromCharCode(65 + opts.randomizers.length),
        perc: 0,
        nextBlockId: '',
      });
      break;
    case 'field-operation':
      if (!opts.fieldOperations) opts.fieldOperations = [];
      opts.fieldOperations.push({
        name: 'set-field-operation',
        group: 'field',
        stepId: uid(),
        options: { parameter: '', value: '' },
      });
      break;
  }
  emit('change');
};
</script>

<template>
  <div class="dc-wrapper">
    <!-- Toolbar (delete + duplicate) - appears on hover, only non-trigger -->
    <div v-if="block.type !== 'trigger'" class="dc-toolbar">
      <button
        type="button"
        class="dc-tb-btn dc-tb-btn-danger"
        @click.stop="emit('remove', id)"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
        </svg>
      </button>
      <button
        type="button"
        class="dc-tb-btn"
        @click.stop="emit('duplicate', id)"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
        </svg>
      </button>
    </div>

    <!-- Target handle (left, not on trigger) -->
    <Handle
      v-if="block.type !== 'trigger'"
      id="in"
      type="target"
      :position="Position.Left"
      class="dc-handle dc-handle-target"
    />

    <!-- THE CARD — bg-white rounded-lg border min-w-64 max-w-76 (DataCrazy exact) -->
    <div
      class="dc-card"
      :class="{ 'dc-card-selected': selected }"
    >
      <div v-if="selected" class="dc-conic-border" />

      <!-- Content: px-4 py-2 (DataCrazy exact) -->
      <div class="dc-content">
        <!-- Header: flex items-center gap-1 mb-2 (DataCrazy exact) -->
        <div class="dc-header">
          <div class="dc-icon" :style="{ color: iconColor }">
            <span :class="definition.icon" class="w-[16px] h-[16px]" />
          </div>
          <span class="dc-title">{{ $t(definition.labelKey) }}</span>
        </div>

        <!-- Description: text-xs (DataCrazy exact) -->
        <p class="dc-desc">{{ $t(definition.descriptionKey) }}</p>

        <!-- Body: mt-2 flex flex-col gap-2 pb-2 (DataCrazy exact) -->
        <div class="dc-body">
          <!-- Sub-items: flex flex-col bg-white gap-2 p-2 rounded-md border (DataCrazy exact) -->
          <div
            v-for="(item, idx) in items"
            :key="idx"
            class="dc-item"
            @click.stop="block.type === 'condition' ? emit('configure-condition', { blockId: id, index: idx }) : null"
          >
            <div class="dc-item-icon" :style="{ color: iconColor }">
              <span :class="item.icon" class="w-[16px] h-[16px]" />
            </div>
            <div class="dc-item-text">
              <span class="dc-item-title">
                <template v-if="item.title">{{ item.title }}</template>
                <template v-else-if="item.text && item.unitKey">
                  {{ item.text }} {{ $t(item.unitKey) }}
                </template>
                <template v-else-if="item.titleKey">{{ $t(item.titleKey) }}</template>
              </span>
              <span v-if="item.sub" class="dc-item-sub">{{ item.sub }}</span>
            </div>
            <button
              v-if="block.type !== 'trigger'"
              type="button"
              class="dc-item-delete"
              @click.stop="removeItem(idx)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>

          <!-- Add button: speech bubble style (DataCrazy exact) -->
          <button
            v-if="canAddStep"
            type="button"
            class="dc-add-btn"
            @click.stop="addStep"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"/><path d="M12 5v14"/>
            </svg>
            <span>{{ $t('FLOWS.INSPECTOR.ADD') }}</span>
          </button>
        </div>
      </div>

      <!-- Handles section (footer area before stats) -->
      <div v-if="handles.length" class="dc-handles">
        <div
          v-for="handle in handles"
          :key="handle.id"
          class="dc-handle-row"
        >
          <span class="dc-handle-label">
            {{ handle.labelKey ? $t(handle.labelKey) : handle.label }}
          </span>
          <Handle
            :id="handle.id"
            type="source"
            :position="Position.Right"
            class="dc-handle dc-handle-source"
            :style="{ borderColor: handleColor(handle.id) }"
          />
        </div>
      </div>

      <!-- Stats: border-t px-4 py-2, flex justify-around (DataCrazy exact) -->
      <div class="dc-stats">
        <div class="dc-stat">
          <span class="dc-stat-val dc-stat-success">{{ stats.success }}</span>
          <span class="dc-stat-lbl">Sucesso</span>
        </div>
        <div class="dc-stat">
          <span class="dc-stat-val dc-stat-alert">{{ stats.alert || 0 }}</span>
          <span class="dc-stat-lbl">Alerta</span>
        </div>
        <div class="dc-stat">
          <span class="dc-stat-val dc-stat-error">{{ stats.error }}</span>
          <span class="dc-stat-lbl">Erro</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Outer wrapper: relative flex justify-center (DataCrazy exact) */
.dc-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
}

/* Toolbar: bg-white rounded-md flex items-center, positioned top (DataCrazy exact) */
.dc-toolbar {
  position: absolute;
  top: -44px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  background: white;
  border-radius: 6px;
  border: 1px solid rgb(var(--slate-4));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 10;
}
.dc-wrapper:hover .dc-toolbar {
  opacity: 1;
  pointer-events: all;
}

/* Toolbar buttons: px-2, text-gray-500 (DataCrazy exact) */
.dc-tb-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  color: rgb(var(--slate-8));
  transition: background 0.12s ease;
}
.dc-tb-btn:first-child {
  border-radius: 6px 0 0 6px;
}
.dc-tb-btn:last-child {
  border-radius: 0 6px 6px 0;
}
.dc-tb-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}
.dc-tb-btn-danger:hover {
  color: rgb(var(--ruby-11));
  background: rgba(var(--ruby-4), 0.15);
}

/* Card: bg-white rounded-lg border min-w-64 max-w-76 (DataCrazy exact) */
.dc-card {
  position: relative;
  background: white;
  border-radius: 8px;
  border: 1px solid rgb(var(--slate-5));
  min-width: 16rem;
  max-width: 19rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.dc-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}
.dc-card-selected {
  border-color: rgb(var(--blue-9));
}

/* Animated conic border for selected state */
.dc-conic-border {
  position: absolute;
  inset: -2.5px;
  border-radius: 10px;
  z-index: -1;
  background: conic-gradient(
    from var(--conic-angle, 0deg),
    rgb(var(--blue-9)),
    rgb(var(--violet-9)),
    rgb(var(--iris-9)),
    rgb(var(--blue-9))
  );
  animation: conic-spin 3s linear infinite;
  opacity: 0.85;
}
@property --conic-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
@keyframes conic-spin {
  to { --conic-angle: 360deg; }
}

/* Content: px-4 py-2 (DataCrazy exact) */
.dc-content {
  padding: 1rem 1rem 0.5rem;
}

/* Header: flex items-center gap-1 mb-2 (DataCrazy exact) */
.dc-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

/* Icon: max-w-5 = w-4 h-4 = 16px, NO background (DataCrazy exact) */
.dc-icon {
  max-width: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Title: H4 component (DataCrazy exact) */
.dc-title {
  font-size: 15px;
  font-weight: 700;
  color: rgb(var(--slate-12));
  line-height: 1.3;
}

/* Description: text-xs (DataCrazy exact) */
.dc-desc {
  font-size: 12px;
  color: rgb(var(--slate-9));
  line-height: 1.4;
}

/* Body: mt-2 flex flex-col gap-2 pb-2 (DataCrazy exact) */
.dc-body {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
}

/* Sub-item: flex flex-col bg-white gap-2 p-2 rounded-md border (DataCrazy exact) */
.dc-item {
  position: relative;
  display: flex;
  padding: 0.5rem;
  gap: 0.5rem;
  align-items: center;
  background: white;
  border: 1px solid rgb(var(--slate-4));
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}
.dc-item:hover {
  background: rgb(var(--slate-2));
  border-color: rgb(var(--blue-9));
}
.dc-item-delete {
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--slate-8));
  opacity: 0;
  transition: all 0.12s ease;
  padding: 0.125rem;
  border-radius: 4px;
}
.dc-item:hover .dc-item-delete {
  opacity: 1;
}
.dc-item-delete:hover {
  color: rgb(var(--ruby-11));
  background: rgba(var(--ruby-4), 0.15);
}

/* Item icon: p-2, 15px (DataCrazy exact) */
.dc-item-icon {
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dc-item-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dc-item-title {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-12));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
}

.dc-item-sub {
  font-size: 11px;
  color: rgb(var(--slate-9));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

/* Add button: speech bubble rounded-full rounded-br-none (DataCrazy exact) */
.dc-add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 9999px;
  border-bottom-right-radius: 0;
  border: 1px dashed rgb(var(--blue-9));
  color: rgb(var(--blue-11));
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}
.dc-add-btn:hover {
  background: rgba(var(--blue-4), 0.05);
}

/* Handles section */
.dc-handles {
  border-top: 1px solid rgb(var(--slate-4));
  padding: 0.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.dc-handle-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  min-height: 1.5rem;
  padding-right: 1.25rem;
  position: relative;
}

.dc-handle-label {
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--slate-10));
  line-height: 1;
}

/* Stats: border-t px-4 py-2, flex justify-around (DataCrazy exact) */
.dc-stats {
  display: flex;
  justify-content: space-around;
  border-top: 1px solid rgb(var(--slate-4));
  padding: 0.5rem 1rem;
}

.dc-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
}

/* Stats value: text-lg (DataCrazy exact) */
.dc-stat-val {
  font-size: 18px;
  font-weight: 700;
  color: rgb(var(--slate-12));
  line-height: 1.2;
}
.dc-stat-success {
  color: rgb(var(--teal-11));
}
.dc-stat-alert {
  color: rgb(var(--amber-11));
}
.dc-stat-error {
  color: rgb(var(--ruby-11));
}

/* Stats label: text-2xs text-primary — ALL SAME DARK COLOR (DataCrazy exact) */
.dc-stat-lbl {
  font-size: 11px;
  font-weight: 500;
  color: rgb(var(--slate-11));
  line-height: 1;
}

/* Handles */
.dc-handle {
  width: 14px !important;
  height: 14px !important;
  background: white !important;
  border: 2px solid rgb(var(--blue-7)) !important;
  border-radius: 50% !important;
  transition: all 0.15s ease;
}
.dc-handle:hover {
  border-color: rgb(var(--blue-9)) !important;
  box-shadow: 0 0 0 4px rgba(var(--blue-4), 0.3);
  transform: scale(1.15);
}

.dc-handle-target {
  border-color: rgb(var(--slate-7)) !important;
  top: 1.25rem !important;
}
.dc-handle-target:hover {
  border-color: rgb(var(--blue-9)) !important;
}

.dc-handle-source {
  position: absolute !important;
  right: -23px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}
</style>
